import { useCallback, useEffect, useRef, useState } from 'react'

export interface Size {
  width: number
  height: number
}

/**
 * Tracks an element's rendered size.
 *
 * The chart is drawn in user units that follow its container, so it has to know
 * its width before it can lay anything out. A ResizeObserver rather than a
 * window resize listener, because the container also changes size when the
 * bottom sheet of controls expands — no window event fires for that.
 */
export function useElementSize<T extends Element>(): [(node: T | null) => void, Size] {
  const [size, setSize] = useState<Size>({ width: 0, height: 0 })
  const observerRef = useRef<ResizeObserver | null>(null)
  const nodeRef = useRef<T | null>(null)

  const ref = useCallback((node: T | null) => {
    nodeRef.current = node
    observerRef.current?.disconnect()
    if (node === null) return

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0]
      if (entry === undefined) return
      const box = entry.contentRect
      setSize((prev) =>
        // Sub-pixel jitter from the observer would otherwise re-render every frame.
        Math.abs(prev.width - box.width) < 0.5 && Math.abs(prev.height - box.height) < 0.5
          ? prev
          : { width: box.width, height: box.height },
      )
    })
    observer.observe(node)
    observerRef.current = observer
  }, [])

  useEffect(() => () => observerRef.current?.disconnect(), [])

  return [ref, size]
}
